/**
 * Preview dev-only de /tesoreria/gastos (Sprint 17 Fase 2).
 */
import { useState } from 'react';
import { GastosView } from '../tesoreria/Gastos';
import type { GastoRead } from '../../lib/api/gastos';
import type { CategoriaRead } from '../../lib/api/types';

type Escenario = 'con_gastos' | 'vacio';

const CATEGORIAS: CategoriaRead[] = [
  { id: 'c1', nombre: 'Mantención', tipo: 'gasto', color: '#5fd9b1', activa: true, categoria_padre_id: null },
  { id: 'c2', nombre: 'Servicios', tipo: 'gasto', color: '#7ab8ff', activa: true, categoria_padre_id: null },
  { id: 'c3', nombre: 'Seguridad', tipo: 'gasto', color: '#ffb775', activa: true, categoria_padre_id: null },
  { id: 'c4', nombre: 'Áreas comunes', tipo: 'gasto', color: '#b89bff', activa: true, categoria_padre_id: null },
  { id: 'c5', nombre: 'Administración', tipo: 'gasto', color: '#ff7a7a', activa: true, categoria_padre_id: null },
];

function hoyISO(): string {
  return new Date().toISOString().slice(0, 10);
}

function diasAtras(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function mockGasto(
  id: string,
  fecha: string,
  proveedor: string,
  descripcion: string,
  cat: string,
  monto: number,
  opts: { anulado?: boolean; compensacion?: boolean } = {},
): GastoRead {
  return {
    id,
    categoria_id: cat,
    proyecto_id: null,
    compensacion_id: opts.compensacion ? `comp-${id}` : null,
    monto,
    fecha,
    proveedor,
    descripcion,
    num_operacion: null,
    comprobante_url: null,
    registrado_por_usuario_id: 'u-mock',
    anulado: !!opts.anulado,
    motivo_anulacion: opts.anulado ? 'mock' : null,
    anulado_at: opts.anulado ? `${fecha}T15:00:00Z` : null,
    anulado_por_usuario_id: null,
    creado_at: `${fecha}T10:00:00Z`,
    actualizado_at: `${fecha}T10:00:00Z`,
  };
}

const ESCENARIOS: Record<Escenario, { gastos: GastoRead[] }> = {
  con_gastos: {
    gastos: [
      mockGasto('g1', hoyISO(), 'Aguas Andinas', 'Cuenta de agua mayo', 'c2', 180_000),
      mockGasto('g2', diasAtras(2), 'Guillermo Pérez', 'Mantención áreas verdes', 'c1', 250_000, { compensacion: true }),
      mockGasto('g3', diasAtras(5), 'Sodimac', 'Repuestos bomba de agua', 'c1', 85_000),
      mockGasto('g4', diasAtras(8), 'Vigilancia ABC', 'Servicio de seguridad mayo', 'c3', 320_000),
      mockGasto('g5', diasAtras(12), 'Empresa Limpieza', 'Aseo áreas comunes', 'c4', 95_000),
      mockGasto('g6', diasAtras(20), 'Contadora', 'Honorarios contables', 'c5', 150_000),
      mockGasto('g7', diasAtras(35), 'Sodimac', 'Pintura cancha', 'c1', 45_000, { anulado: true }),
      mockGasto('g8', diasAtras(60), 'Aguas Andinas', 'Cuenta agua marzo', 'c2', 165_000),
    ],
  },
  vacio: { gastos: [] },
};

const SIDEBAR_ITEMS = [
  { label: 'Inicio' },
  { label: 'Casas' },
  { label: 'Pagos' },
  { label: 'Banco' },
  { label: 'Ingresos varios' },
  { label: 'Gastos', active: true },
  { label: 'Compensaciones' },
  { label: 'Recursos' },
  { label: 'Cuotas', proximamente: true },
  { label: 'Suspensiones', proximamente: true },
  { label: 'Cierres', proximamente: true },
  { label: 'Reportes', proximamente: true },
];

export function TesoreriaGastosPreview() {
  const [escenario, setEscenario] = useState<Escenario>('con_gastos');
  const data = ESCENARIOS[escenario];

  return (
    <div className="min-h-screen bg-bg-page text-text-primary flex">
      <aside className="hidden md:flex w-64 bg-bg-sunken border-r-[0.5px] border-white/[0.08] flex-col">
        <div className="px-6 py-5 border-b-[0.5px] border-white/[0.08]">
          <div className="text-xs uppercase tracking-widest text-orange font-medium">
            Tesorería
          </div>
          <div className="text-base text-text-primary font-medium mt-1">
            Barrio Calbucura
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {SIDEBAR_ITEMS.map((it) => (
            <div
              key={it.label}
              className={`flex items-center justify-between px-3 py-2 rounded text-sm ${
                it.active
                  ? 'bg-bg-card text-text-primary font-medium'
                  : 'text-text-secondary'
              } ${it.proximamente ? 'opacity-70' : ''}`}
            >
              <span>{it.label}</span>
              {it.proximamente && (
                <span className="text-[10px] uppercase tracking-wide text-text-muted">
                  pronto
                </span>
              )}
            </div>
          ))}
        </nav>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="bg-bg-card border-b-[0.5px] border-white/[0.08] px-4 md:px-8 py-3 flex items-center justify-between gap-4">
          <div className="text-sm text-text-secondary">
            Panel del tesorero · Gastos
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-text-muted hidden sm:inline">Escenario:</span>
            {(['con_gastos', 'vacio'] as const).map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => setEscenario(e)}
                className={`px-2 py-1 rounded border ${
                  escenario === e
                    ? 'border-orange text-orange'
                    : 'border-white/[0.12] text-text-secondary hover:text-text-primary'
                }`}
              >
                {e}
              </button>
            ))}
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <GastosView
            gastos={data.gastos}
            categorias={CATEGORIAS}
            resumen={{
              anio_activo: 2026,
              total_mes: data.gastos
                .filter((g) => !g.anulado)
                .reduce((s, g) => s + g.monto, 0),
              total_anio: data.gastos
                .filter((g) => !g.anulado)
                .reduce((s, g) => s + g.monto, 0),
              top_categorias: CATEGORIAS.slice(0, 3).map((c) => ({
                categoria_id: c.id,
                nombre: c.nombre,
                color: c.color,
                monto: 50000,
              })),
            }}
            loading={false}
            error={null}
            onCrear={async () => {
              alert('Mock: gasto creado (preview dev)');
            }}
            onAnular={async () => {
              alert('Mock: gasto anulado (preview dev)');
            }}
            onActualizado={() => {
              alert('Mock: gasto actualizado (preview dev)');
            }}
          />
        </main>

        <footer className="bg-bg-page px-4 md:px-8 py-3 text-[11px] text-text-muted border-t border-white/[0.06]">
          Preview dev-only · Crear/anular muestran alert en lugar de POST real.
        </footer>
      </div>
    </div>
  );
}
